import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { sdh_refineInteractive, sdh_previewOptions, sdh_applyDecisions, ASSESSMENT_BY_VALUE,
  type RefineProposal, type RefineOption,
         type RefineTurn, type OptionImpact, type EnrichedRule } from "../api/sdhAPI";
import SampleRecordsPanel from "./SampleRecordsPanel";
import RuleLogicView from "./RuleLogicView";

/**
 * Rule Studio — the runtime SME feedback loop for ONE rule.
 * She types what she wants changed, the agent proposes a revision, she sees a before/after diff and the
 * agent's explanation. Nothing is saved until she clicks Save (persist=true), so she can keep re-steering
 * with different wording until the rule says what she means.
 */
const errText = (e: any, fallback: string) =>
  e?.response?.data?.detail || e?.response?.data?.message || e?.data?.detail || e?.message || fallback;

const FIELDS: Array<[string, string]> = [
  ["constraint_family", "Rule type (family)"],
  ["condition", "Condition (WHEN)"],
  ["predicate_template", "Predicate (THEN) — executable"],
  ["expected_outcome", "Expected outcome (THEN)"],
  ["business_logic", "Plain-English meaning"],
  ["severity", "Severity"],
];

const QUICK = [
  "Bind this to the governed Collibra values; obsolete codes are not allowed.",
  "Only applies to finished goods (MTART = FERT).",
  "This is not mandatory for all material types — narrow the scope.",
  "The rule flags too much — tighten it so it only catches real exceptions.",
];

export default function RuleStudioPanel(
  { entityId, appNm, rule, onClose, onSaved }:
  { entityId: string; appNm: string; rule: EnrichedRule; onClose: () => void; onSaved: (m: string) => void }
) {
  const stored = (rule as any).sme_decision?.comment || "";
  const [directive, setDirective] = useState<string>(stored);
  const [prop, setProp] = useState<RefineProposal | null>(null);
  const [opts, setOpts] = useState<RefineOption[]>([]);
  const [pick, setPick] = useState<string | null>(null);
  const [interp, setInterp] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [question, setQuestion] = useState<string | null>(null);
  const [before, setBefore] = useState<Record<string, any> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tries, setTries] = useState(0);
  // The agent is stateless, so the panel keeps the exchange and replays it on every ask. That is how a
  // follow-up answer ("FERT and HALB") is understood as the reply to the agent's last question.
  const [convo, setConvo] = useState<RefineTurn[]>([]);
  const { user } = useAuth();
  const [showAccept, setShowAccept] = useState(false);
  // R5: saving a refinement never forces a verdict - the default keeps her current decision; a comment is
  // required only when she CHANGES the status (the audit trail needs the reason then)
  const [acceptGate, setAcceptGate] = useState("KEEP");       // APPROVE | PENDING | KEEP
  const [decisionComment, setDecisionComment] = useState("");   // her comment for THIS version
  const [impact, setImpact] = useState<Record<string, OptionImpact>>({});
  const [impBusy, setImpBusy] = useState(false);
  const [samplesFor, setSamplesFor] = useState<string | null>(null);   // option label

  const ask = async () => {
    if (!directive.trim()) { setErr("Tell the agent what you want changed."); return; }
    setBusy(true); setErr(null);
    try {
      const res = await sdh_refineInteractive(entityId, appNm, rule.rule_id, directive.trim(), false, 2,
        undefined, convo);
      if (!res.proposal) {
        // Say WHY. "try rewording" is useless when the real cause is a rejected predicate.
        setErr(res.reason || (res.status === "no_refinement"
          ? "The agent returned no proposal for this rule. Try naming the fields and the condition explicitly."
          : res.status));
        setProp(null);
      }
      else {
        const list = res.options || res.proposal.options || [];
        setProp(res.proposal); setOpts(list); setBefore(res.before || null);
        setInterp(res.interpretation || res.proposal.interpretation || null);
        setChallenge(res.challenge || res.proposal.challenge || null);
        setQuestion(res.question_for_sme || res.proposal.question_for_sme || null);
        setPick((list.find((o) => o.recommended) || list[0])?.label ?? null);
        // record this turn so the next ask carries the thread
        const agentSaid = [res.interpretation || res.proposal.interpretation,
          list.length ? `Options: ${list.map((o) => o.label).join(" | ")}` : "",
          res.question_for_sme || res.proposal.question_for_sme || ""]
          .filter(Boolean).join(" ");
        setConvo((c) => [...c, { role: "sme", content: directive.trim() },
                                { role: "agent", content: agentSaid }]);
        setTries((t) => t + 1);
        // Run every option on sample data so she can compare impact before choosing.
        if (list.length) {
          setImpBusy(true); setImpact({});
          sdh_previewOptions(entityId, appNm, rule.rule_id, list)
            .then((r) => {
              const m: Record<string, OptionImpact> = {};
              (r.options || []).forEach((o) => { if (o.label) m[o.label] = o; });
              setImpact(m);
            })
            .catch(() => {})
            .finally(() => setImpBusy(false));
        }
      }
    } catch (e: any) { setErr(errText(e, "Refine failed — see the server log for details.")); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      // STEP 1 — persist the revised rule content (bumps the version). Refine only touches the rule,
      // never the decision. Unchanged from before.
      const res = await sdh_refineInteractive(entityId, appNm, rule.rule_id, directive.trim(), true, 2,
        pick || undefined, convo, prop || undefined);

      // STEP 2 — record HER decision + comment on the new version, via the SAME path the Details
      // modal uses. The agent's action (KEEP/REVISE) and her decision are INDEPENDENT:
      //   Approve  → gate APPROVED
      //   Pending  → gate PENDING
      //   Keep as-is → keep whatever gate she already had (she's just adding a comment / re-confirming)
      const currentGate = ((rule as any).sme_decision?.decision_gate || "PENDING").toUpperCase();
      const gate = acceptGate === "APPROVE" ? "APPROVED"
                 : acceptGate === "PENDING" ? "PENDING"
                 : currentGate;   // KEEP → her existing verdict
      if (decisionComment.trim()) {
        const def = Object.values(ASSESSMENT_BY_VALUE).find((a: any) => a.gate === gate)
                 || ASSESSMENT_BY_VALUE[(rule as any).sme_decision?.assessment || ""];
        await sdh_applyDecisions(entityId, appNm, [{
          constraint_id: rule.rule_id, decision_type: "rule_review",
          choice: def?.choice, assessment: def?.value, decision_gate: gate,
          comment: decisionComment.trim(),
          reviewed_version: res.version,
          sme_id: user?.user_id || user?.email || "unknown",
          sme_name: user?.full_name || user?.email || undefined,
        }]);
      }

      setAcceptGate("KEEP"); setDecisionComment("");
      onSaved(`Saved as iteration 2 — ${rule.rule_name || rule.rule_id} is now v${res.version ?? "?"}.`);
    } catch (e: any) { setErr(errText(e, "Save failed — see the server log for details.")); }
    finally { setBusy(false); }
  };

  const sel: RefineOption | undefined = opts.find((o) => o.label === pick) || opts[0];
  const rev: any = (sel?.revised as any) || prop?.revised || {};
  const act = sel?.action || prop?.action;
  const beforeOf = (k: string) => {
    const v = (before || {})[k];
    return v == null || v === "" ? "" : String(v);   // "" renders as "not set", never the enriched value
  };
  const changed = FIELDS.filter(([k]) => {
    const to = (rev as any)[k];
    return to != null && String(to).trim() !== "" && String(to) !== beforeOf(k);
  });

  // ---------- redesigned layout (R6): sticky outcome header, conversation | options-as-tabs, sticky decision footer
  const CUR = "Current rule (unchanged)";
  const curLogic = (rule as any).logic || (rule as any)._raw?.logic || (rule as any).content?.logic;
  const curIm = impact[CUR];
  // KEEP changes nothing -> its numbers ARE the current rule's (preview does not re-measure it)
  const selIm = sel ? (sel.action === "KEEP" ? curIm : impact[sel.label]) : undefined;
  const selLogic = (sel as any)?.revised?.logic;
  const diff = selLogic ? logicDiff(curLogic, selLogic) : null;
  const sameLogic = !!selLogic && canon(selLogic) === canon(curLogic);
  const delta = (curIm?.violating != null && selIm?.violating != null) ? selIm.violating - curIm.violating : null;
  const fmtN = (m: any) => !m ? "—" : m.status === "not_compilable" ? "cannot run"
    : m.violating == null ? "not measured" : `${m.violating.toLocaleString()} of ${(m.applicable ?? 0).toLocaleString()}`;
  const canSave = !busy && !!sel && sel.action !== "KEEP" && !!acceptGate && (acceptGate === "KEEP" || !!decisionComment.trim());

  return (
    <div className="rs-scrim">
      <style>{CSS}</style>
      <div className="rs2" role="dialog" aria-label="Refine with agent">
        <header className="rs2-h">
          <div className="t">
            <b>{rule.rule_name || rule.rule_id}</b>
            <span className="sub">{(rule as any).owner_field_id || ""}</span>
          </div>
          <div className="outcome">
            <span className="k">On your data</span>
            <span className="cur">now <b>{fmtN(curIm)}</b> flagged</span>
            {sel && sel.action !== "KEEP" && <>
              <span className="arrow">→</span>
              <span className="new">{sel.label}: <b>{fmtN(selIm)}</b></span>
              {delta != null && delta !== 0 && <span className={"delta " + (delta < 0 ? "down" : "up")}>{delta > 0 ? "+" : ""}{delta.toLocaleString()}</span>}
            </>}
          </div>
          <button className="x" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="rs2-body">
          {/* LEFT - the conversation */}
          <aside className="conv">
            <label className="lbl">What should change?</label>
            <textarea value={directive} onChange={(e) => setDirective(e.target.value)} rows={4}
              placeholder="e.g. 'only for finished goods', 'code 26 is valid', 'too many false alarms'" />
            <div className="chips">
              {QUICK.map((q) => <button key={q} className="chip" title={q} onClick={() => setDirective(q)}>{q}</button>)}
            </div>
            <div className="ask">
              <button className="btn pri" disabled={busy} onClick={ask}>{busy ? "Thinking…" : tries ? "Ask again" : "Ask the agent"}</button>
              <span className="mut">{tries ? `attempt ${tries} — nothing is saved yet` : "nothing is saved until you apply an option"}</span>
            </div>
            {err && <div className="err">{err}</div>}
            {(interp || challenge || question) && <div className="notes">
              {interp && <details><summary>🔍 <b>Agent's analysis</b> <span className="pv">{short(interp)}</span></summary><p>{interp}</p></details>}
              {challenge && <details className="warn"><summary>⚑ <b>Evidence disagrees with your assumption</b> <span className="pv">{short(challenge)}</span></summary><p>{challenge}</p></details>}
              {question && <details open className="ask-q"><summary>? Question for you</summary><p>{question}</p><em>Answer in the box above and ask again.</em></details>}
            </div>}
            {convo.length > 0 && <details className="turns"><summary>{Math.floor(convo.length / 2)} earlier turn(s)</summary>
              {convo.map((t, i) => <div key={i} className={"turn " + t.role}><b>{t.role === "sme" ? "You" : "Agent"}</b> {t.content}</div>)}
            </details>}
          </aside>

          {/* RIGHT - options as tabs */}
          <main className="opts2">
            {!opts.length ? <div className="empty">Tell the agent what should change, then ask. Its options appear here, each measured on your data.</div> : <>
              <div className="tabs">
                {opts.map((o) => { const im = impact[o.label];
                  return <button key={o.label} className={"tab" + (o.label === pick ? " on" : "")} onClick={() => setPick(o.label)}>
                    <span className="tl">{o.label}</span>
                    <span className="tm"><i className={"act " + String(o.action).toLowerCase()}>{o.action}</i>
                      {o.recommended && <i className="rec">★</i>}
                      {o.confidence != null && <i className="cf">{Math.round(Number(o.confidence) * (Number(o.confidence) <= 1 ? 100 : 1))}%</i>}
                      <i className="fl">{o.action === "KEEP" ? "unchanged" : impBusy && !im ? "measuring…" : im?.violating != null ? `${im.violating.toLocaleString()} flagged` : ""}</i></span>
                  </button>; })}
              </div>
              {sel && <section className="pane">
                <h4>What changes</h4>
                {sel.action === "KEEP" ? <p className="mut">The agent recommends keeping the rule as it is.</p>
                 : sel.action === "DROP" ? <p className="bad">The agent recommends dropping this rule.</p>
                 : diff && (diff.added.length || diff.removed.length) ? <div className="codes">
                    {diff.added.map((c) => <span key={"a" + c} className="code add">+{c}</span>)}
                    {diff.removed.map((c) => <span key={"r" + c} className="code rem">−{c}</span>)}
                    <span className="mut">{diff.kept} code(s) unchanged</span>
                  </div>
                 : sameLogic ? <p className="warnl">This option executes <b>exactly the same logic</b> as the current rule — only its texts would change.</p>
                 : selLogic ? <p className="mut">The logic changes (not only its code list) — compare below.</p>
                 : <p className="bad">⚠ This option has no executable logic — it would change only the texts.</p>}
                {selLogic && <details className="cmp"><summary>Compare what executes (current vs this option)</summary>
                  <div className="two"><div><em>Current</em><RuleLogicView rule={{ logic: curLogic }} compact /></div>
                    <div><em>{sel.label}</em><RuleLogicView rule={{ logic: selLogic }} compact /></div></div></details>}

                <h4>Impact on your data</h4>
                <div className="imp">
                  <div><em>now</em><b>{fmtN(curIm)}</b> flagged</div>
                  <div className="ar">→</div>
                  <div><em>with this option</em><b>{fmtN(selIm)}</b> flagged</div>
                  {sel.action === "KEEP" ? <span className="delta">unchanged</span>
                    : delta != null && <span className={"delta " + (delta < 0 ? "down" : delta > 0 ? "up" : "")}>{delta === 0 ? "same result as now" : (delta > 0 ? "+" : "") + delta.toLocaleString()}</span>}
                  {selIm && (selIm.sample_failing?.length || selIm.sample_passing?.length) ?
                    <button className="btn sm" onClick={() => setSamplesFor(sel.label)}>inspect rows</button> : null}
                </div>
                {((selIm as any)?.warnings || []).map((w: string, i: number) => <div key={i} className="warnl">⚠ {w}</div>)}

                <div className="why">
                  {sel.rationale && <details><summary><b>Why</b> {short(sel.rationale)}</summary><p>{sel.rationale}</p></details>}
                  {(sel as any).trade_off && <details><summary><b>Trade-off</b> {short((sel as any).trade_off)}</summary><p>{(sel as any).trade_off}</p></details>}
                  {(sel as any).sme_action && <details open><summary><b>Action needed from you</b></summary><p>{(sel as any).sme_action}</p></details>}
                </div>
                {(sel as any).revised && <details className="texts"><summary>Title and description (generated from the logic when applied)</summary>
                  {["rule_title", "business_summary", "business_detail"].map((k) => (sel as any).revised[k] ?
                    <div key={k} className="tx"><em>{k.replace("_", " ")}</em> {(sel as any).revised[k]}</div> : null)}
                </details>}
              </section>}
            </>}
          </main>
        </div>

        <footer className="rs2-f">
          <div className="seg" role="radiogroup" aria-label="Your decision">
            {[["KEEP", "Keep my decision"], ["APPROVE", "Approve"], ["PENDING", "Pending"]].map(([k, l]) =>
              <button key={k} className={acceptGate === k ? "on" : ""} onClick={() => setAcceptGate(k)}>{l}</button>)}
          </div>
          <input className="cm" value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)}
            placeholder={acceptGate === "KEEP" ? "Comment (optional)" : "Why are you changing the status? (required)"} />
          <button className="btn" onClick={onClose}>Discard</button>
          <button className="btn pri" disabled={!canSave} onClick={save}
            title={!sel ? "Ask the agent and choose an option first" : sel.action === "KEEP" ? "This option changes nothing"
              : "The applied option becomes the active version; your previous version stays in the history"}>
            {busy ? "Saving…" : sel ? `Apply “${sel.label}”` : "Apply option"}</button>
          {sel && sel.action === "KEEP" && <span className="mut">Keep = no change, nothing to apply</span>}
        </footer>
      </div>
      {samplesFor && (() => {
        const im = impact[samplesFor];
        if (!im) return null;
        const flds = ((rule as any)._raw?.participating_fields || [])
          .map((p: any) => p.field_id).filter(Boolean);
        return <div className="rs-samples"><SampleRecordsPanel
          title={`${samplesFor} — ${rule.rule_name || rule.rule_id}`}
          subtitle={im.violating != null
            ? `${im.violating.toLocaleString()} of ${(im.applicable ?? 0).toLocaleString()} flagged on your data`
            : undefined}
          fails={im.sample_failing} passes={im.sample_passing}
          ruleFields={flds} onClose={() => setSamplesFor(null)} /></div>;
      })()}
    </div>
  );
}

const short = (s: any) => { const t = String(s || ""); return t.length > 90 ? t.slice(0, 90) + "…" : t; };
/** order-insensitive JSON of a logic tree, to tell "same logic" from "changed" */
const canon = (x: any): string => x == null ? "" : Array.isArray(x) ? "[" + x.map(canon).join(",") + "]"
  : typeof x === "object" ? "{" + Object.keys(x).filter((k) => !(k === "numeric" && x[k] === false)).sort()
      .map((k) => k + ":" + canon(x[k])).join(",") + "}" : JSON.stringify(x);

/** Codes a rule tests: value lists, and the alternatives of a code-list pattern (e.g. ^(?:-|10|11|26|BA)...). */
function codeSet(lg: any): Set<string> {
  const out = new Set<string>();
  const walk = (c: any) => {
    if (!c || typeof c !== "object") return;
    (c.all || []).forEach(walk); (c.any || []).forEach(walk); if (c.not) walk(c.not);
    (Array.isArray(c.values) ? c.values : []).forEach((v: any) => out.add(String(v)));
    if (c.value != null && ["eq", "ne"].includes(String(c.op))) out.add(String(c.value));
    if (c.op === "matches" && typeof c.pattern === "string") {
      const m = c.pattern.match(/\(\?:([^()]*)\)/);
      if (m) m[1].split("|").map((x: string) => x.replace(/\\/g, "")).filter((x: string) => /^[A-Za-z0-9-]{1,6}$/.test(x)).forEach((x: string) => out.add(x));
    }
  };
  if (lg) { walk(lg.when); walk(lg.then); walk(lg.assert); }
  return out;
}
/** Code-level difference between the current and the proposed logic (what the SME needs to see). */
function logicDiff(a: any, b: any): { added: string[]; removed: string[]; kept: number } {
  const A = codeSet(a), B = codeSet(b);
  return { added: [...B].filter((x) => !A.has(x)).sort(), removed: [...A].filter((x) => !B.has(x)).sort(),
           kept: [...B].filter((x) => A.has(x)).length };
}

const CSS = `
.rs-scrim{position:fixed;inset:0;background:rgba(15,25,35,.5);z-index:3000;display:flex;align-items:center;justify-content:center;padding:12px}
.rs2{background:#fff;width:min(1440px,100%);height:calc(100vh - 24px);max-height:900px;border-radius:10px;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 18px 50px rgba(0,0,0,.3);font-size:13px;color:#1d2b36}
.rs2-h{display:flex;align-items:center;gap:14px;padding:8px 14px;background:linear-gradient(90deg,#460073 0%,#7500C0 55%,#A100FF 100%);border-bottom:3px solid #A100FF;color:#fff;flex:none}
.rs2-h .t{display:flex;flex-direction:column;min-width:0;flex:1}.rs2-h .t b{font-size:15px}.rs2-h .sub{opacity:.7;font-family:monospace;font-size:11px}
.rs2-h .outcome{display:flex;align-items:center;gap:8px;background:rgba(255,255,255,.08);padding:6px 10px;border-radius:8px}
.rs2-h .outcome .k{opacity:.7;font-size:11px;text-transform:uppercase;letter-spacing:.04em}.rs2-h .arrow{opacity:.7}
.rs2-h .x{background:none;border:0;color:#fff;font-size:22px;cursor:pointer}
.delta{font-weight:700;padding:2px 8px;border-radius:10px;font-size:12px;background:#eef1f4;color:#1d2b36}.delta.down{background:#e3f4ea;color:#1f6b40}.delta.up{background:#fde8e6;color:#9b2b1f}
.rs2-body{flex:1;display:grid;grid-template-columns:minmax(260px,30%) 1fr;min-height:0}
.conv{border-right:1px solid #e6eaee;padding:10px 12px;overflow:auto;background:#fafbfc}
.notes summary .pv{font-weight:400;color:#5b6b78}.notes details[open] summary .pv{display:none}
.conv .lbl{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#5b6b78;font-weight:600}
.conv textarea{width:100%;margin-top:6px;border:1px solid #cfd7de;border-radius:6px;padding:8px;font:inherit;resize:vertical}
.chips{display:flex;flex-wrap:wrap;gap:6px;margin:8px 0}.chip{border:1px solid #d6dde3;background:#fff;border-radius:14px;padding:3px 10px;font-size:11.5px;cursor:pointer;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ask{display:flex;align-items:center;gap:10px;margin:6px 0 10px}.mut{color:#6b7a86;font-size:12px}.bad{color:#9b2b1f}
.err{background:#fde8e6;color:#9b2b1f;padding:8px;border-radius:6px;margin-bottom:8px}
.notes details{border-left:3px solid #4a7fb0;background:#f1f6fb;border-radius:4px;padding:6px 10px;margin:6px 0}.notes details.warn{border-color:#c0392b;background:#fdf1f0}.notes details.ask-q{border-color:#c98a1b;background:#fdf6e9}
.notes summary{cursor:pointer;font-weight:600}.notes p{margin:6px 0 2px;line-height:1.45}
.turns{margin-top:8px}.turns summary{cursor:pointer;color:#43607e}.turn{padding:4px 0;border-bottom:1px dashed #e3e7ea}.turn.agent b{color:#43607e}
.opts2{display:flex;flex-direction:column;min-height:0}
.empty{margin:auto;max-width:360px;text-align:center;color:#6b7a86;line-height:1.5}
.tabs{display:flex;gap:6px;padding:10px 14px 0;border-bottom:1px solid #e6eaee;overflow-x:auto}
.tab{border:1px solid #d6dde3;border-bottom:0;background:#f4f6f8;border-radius:8px 8px 0 0;padding:7px 12px;cursor:pointer;text-align:left;min-width:180px;max-width:280px}
.tab.on{background:#fff;border-color:#2f7a6a;box-shadow:inset 0 3px 0 #2f7a6a}.tab .tl{display:block;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tab .tm{display:flex;gap:6px;margin-top:3px;font-size:11px;font-style:normal}.tab i{font-style:normal}
.act{padding:0 6px;border-radius:8px;background:#eef1f4}.act.revise{background:#e3f1ee;color:#1f6b5a}.act.keep{background:#eef1f8;color:#43607e}.act.drop{background:#fde8e6;color:#9b2b1f}
.rec{color:#c98a1b}.cf{color:#6b7a86}.fl{color:#1d2b36;margin-left:auto}
.pane{padding:10px 14px;overflow:auto;flex:1;min-height:0}.pane h4{margin:14px 0 6px;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#5b6b78}.pane h4:first-child{margin-top:0}
.codes{display:flex;flex-wrap:wrap;gap:6px;align-items:center}.code{font-family:monospace;padding:2px 8px;border-radius:10px;font-weight:700}.code.add{background:#e3f4ea;color:#1f6b40}.code.rem{background:#fde8e6;color:#9b2b1f;text-decoration:line-through}
.cmp{margin-top:8px}.cmp summary{cursor:pointer;color:#43607e}.two{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:6px}.two em{font-size:11px;color:#6b7a86}
.imp{display:flex;align-items:center;gap:14px;background:#f6f8fa;border-radius:8px;padding:10px 12px}.imp em{display:block;font-size:11px;color:#6b7a86;font-style:normal}.imp b{font-size:15px}.imp .ar{color:#8a97a3}
.warnl{color:#9b5b00;background:#fdf6e9;border-radius:6px;padding:5px 8px;margin-top:6px}
.why{margin-top:12px;display:flex;flex-direction:column;gap:6px}.why details{border:1px solid #e6eaee;border-radius:6px;padding:6px 10px}.why summary{cursor:pointer}.why p{margin:6px 0 2px;line-height:1.45}
.texts{margin-top:12px;color:#43607e}.texts summary{cursor:pointer}.tx{padding:4px 0;color:#1d2b36}.tx em{color:#6b7a86;font-style:normal;text-transform:capitalize;margin-right:6px}
.rs2-f{display:flex;align-items:center;gap:8px;padding:8px 14px;border-top:1px solid #e6eaee;background:#fafbfc;flex:none}
.seg{display:flex;border:1px solid #cfd7de;border-radius:6px;overflow:hidden}.seg button{border:0;background:#fff;padding:6px 10px;cursor:pointer}.seg button.on{background:#0f2235;color:#fff}
.cm{flex:1;min-width:200px;border:1px solid #cfd7de;border-radius:6px;padding:6px 8px;font:inherit}
.btn{border:1px solid #cfd7de;background:#fff;border-radius:6px;padding:6px 12px;cursor:pointer;font:inherit}.btn.pri{background:#2f7a6a;color:#fff;border-color:#2f7a6a}.btn:disabled{opacity:.5;cursor:not-allowed}.btn.sm{padding:3px 8px;font-size:12px}
.tabs{flex:none}
.rs-samples .sr-scrim{z-index:3100}
`;
